import React, { useState } from "react";
import {
  ExclamationTriangleIcon,
  CalendarIcon,
  ShieldExclamationIcon,
  ComputerDesktopIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  TagIcon,
  UserIcon,
  LockClosedIcon,
  LockOpenIcon,
  ClockIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import ApiLoader from "../ui/ApiLoader";

const PreviousReportsSection = ({
  loading,
  previousReports,
  getSeverityBadge,
  getStatusBadge,
  formatDate,
}) => {
  const [expandedReportId, setExpandedReportId] = useState(null);
  const toggleReport = (id) =>
    setExpandedReportId((prev) => (prev === id ? null : id));

  return (
    <div className="mt-10">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-6">
          Your Previous Reports
        </h2>

        {loading ? (
          <ApiLoader label="Loading your reports..." />
        ) : previousReports.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <ShieldExclamationIcon className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>No previous incident reports found.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {previousReports.map((report, index) => {
              const rowId = report.id || index;
              const isExpanded = expandedReportId === rowId;
              return (
                <div
                  key={rowId}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* ── Summary row ── */}
                  <button
                    type="button"
                    onClick={() => toggleReport(rowId)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500">
                        <ExclamationTriangleIcon className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                          {report.incident_no}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                          {report.title}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${getSeverityBadge(
                          report.severity
                        )}`}
                      >
                        {report.severity}
                      </span>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusBadge(
                          report.status
                        )}`}
                      >
                        {report.status?.replace(/_/g, " ") || "Reported"}
                      </span>
                      {/* Closed / Open indicator */}
                      {report.status === "closed" || report.status === "false_alarm" ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-700 text-white dark:bg-gray-600">
                          <LockClosedIcon className="w-3 h-3" />
                          {report.status === "false_alarm" ? "False Alarm" : "Closed"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-white dark:bg-blue-900">
                          <LockOpenIcon className="w-3 h-3" />
                          Open
                        </span>
                      )}
                      {/* Last updated date */}
                      {report.updated_at && (
                        <div className="flex items-center text-xs text-gray-400 dark:text-gray-500 gap-1">
                          <ClockIcon className="w-3.5 h-3.5" />
                          <span>Updated: {formatDate(report.updated_at)}</span>
                        </div>
                      )}
                      {isExpanded ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* ── Expanded detail panel ── */}
                  {isExpanded && (
                    <div className="px-5 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Left column */}
                        <div className="space-y-3">
                          <DetailField
                            icon={<TagIcon className="w-3.5 h-3.5" />}
                            label="Incident No"
                            value={report.incident_no}
                          />
                          <DetailField
                            icon={<ShieldExclamationIcon className="w-3.5 h-3.5" />}
                            label="Category"
                            value={report.title}
                          />
                          <DetailField
                            icon={<TagIcon className="w-3.5 h-3.5" />}
                            label="Sub-type"
                            value={
                              (report.affected_systems && report.affected_systems !== report.incident_type)
                                ? report.affected_systems
                                : report.incident_type?.replace(/_/g, " ")
                            }
                          />
                          <DetailField
                            icon={<UserIcon className="w-3.5 h-3.5" />}
                            label="Reporter"
                            value={report.reporter}
                          />
                        </div>

                        {/* Right column */}
                        <div className="space-y-3">
                          <DetailField
                            icon={<CalendarIcon className="w-3.5 h-3.5" />}
                            label="Reported On"
                            value={formatDate(
                              report.reported_date || report.created_at
                            )}
                          />
                          {report.updated_at && (
                            <DetailField
                              icon={<ClockIcon className="w-3.5 h-3.5" />}
                              label="Last Updated"
                              value={formatDate(report.updated_at)}
                            />
                          )}
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                              Severity
                            </p>
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${getSeverityBadge(
                                report.severity
                              )}`}
                            >
                              {report.severity}
                            </span>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                              Status
                            </p>
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusBadge(
                                report.status
                              )}`}
                            >
                              {report.status?.replace(/_/g, " ") || "Reported"}
                            </span>
                          </div>
                          {report.affected_systems && (
                            <DetailField
                              icon={<ComputerDesktopIcon className="w-3.5 h-3.5" />}
                              label="Affected Systems"
                              value={report.affected_systems}
                            />
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {report.description && (
                        <div className="mt-4">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5 flex items-center gap-1">
                            <DocumentTextIcon className="w-3.5 h-3.5" />
                            Description
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 whitespace-pre-wrap">
                            {report.description}
                          </p>
                        </div>
                      )}

                      {/* Attachments */}
                      {report.attachments && report.attachments.length > 0 && (
                        <div className="mt-4">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1">
                            <PaperClipIcon className="w-3.5 h-3.5" />
                            Attachments ({report.attachments.length})
                          </p>
                          <ul className="space-y-1.5">
                            {report.attachments.map((url, i) => {
                              const filename = url.split('/').pop().split('?')[0];
                              const ext = filename.split('.').pop().toLowerCase();
                              const isImage = ['jpg','jpeg','png','gif','webp','svg'].includes(ext);
                              const isPdf = ext === 'pdf';
                              const isOffice = ['doc','docx','xls','xlsx','ppt','pptx'].includes(ext);
                              // Office docs → Google Docs Viewer; images & PDFs → direct inline URL
                              const viewUrl = isOffice
                                ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
                                : url;
                              return (
                                <li key={i}>
                                  <a
                                    href={viewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group"
                                  >
                                    <PaperClipIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                                    <span className="text-xs text-blue-600 dark:text-blue-400 truncate group-hover:underline flex-1">
                                      {filename}
                                    </span>
                                    <span className="text-[10px] uppercase font-semibold text-gray-400 flex-shrink-0">
                                      {isOffice ? 'view' : isPdf ? 'pdf' : isImage ? 'image' : ext}
                                    </span>
                                    {isImage && (
                                      <img
                                        src={url}
                                        alt={filename}
                                        className="h-8 w-8 object-cover rounded border border-gray-200 dark:border-gray-600 flex-shrink-0"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    )}
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Helper ──────────────────────────────────────────────────
const DetailField = ({ icon, label, value }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5 flex items-center gap-1">
      {icon}
      {label}
    </p>
    <p className="text-xs font-medium text-gray-800 dark:text-gray-200 capitalize">
      {value || <span className="text-gray-400 italic">—</span>}
    </p>
  </div>
);

export default PreviousReportsSection;
